import React from "react";
import ActionGroup from "../components/ActionGroup";
import FaqList from "../components/FaqList";
import SiteLayout from "../components/SiteLayout";
import TestimonialCard from "../components/TestimonialCard";
import {
  homeFaqs,
  homeHeroActions,
  homeIntroductionCards,
  homeHeroMosaicVideos,
  homeHeroServiceLabels,
  homeTransformationGallery,
  lizzieStory,
  testimonials,
} from "../siteContent";

const IntroductionIcon = ({ icon }) => {
  if (icon === "palette") {
    return (
      <svg aria-hidden="true" viewBox="0 0 24 24">
        <path
          d="M12 3.5C7.3 3.5 3.5 7 3.5 11.4c0 3.3 2.5 5.8 5.7 5.8h1.4c.7 0 1.2.5 1.2 1.1 0 1.3 1.1 2.2 2.5 2.2 3.5 0 6.2-2.4 6.2-6.4C20.5 7.7 16.8 3.5 12 3.5Z"
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.8"
        />
        <circle cx="8.4" cy="10" r="1" fill="currentColor" />
        <circle cx="11.4" cy="7.8" r="1" fill="currentColor" />
        <circle cx="15.2" cy="8.7" r="1" fill="currentColor" />
        <circle cx="15.8" cy="12.5" r="1" fill="currentColor" />
      </svg>
    );
  }

  if (icon === "blend") {
    return (
      <svg aria-hidden="true" viewBox="0 0 24 24">
        <circle cx="7.5" cy="6.5" fill="none" r="3" stroke="currentColor" strokeWidth="1.8" />
        <circle cx="7.5" cy="17.5" fill="none" r="3" stroke="currentColor" strokeWidth="1.8" />
        <path
          d="M10 8.5 18 16.5M10 15.5l8-8"
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeWidth="1.8"
        />
      </svg>
    );
  }

  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path
        d="m12 4 1.8 4.7L18.5 10l-4.7 1.3L12 16l-1.8-4.7L5.5 10l4.7-1.3L12 4ZM18.5 4.5l.8 2 .8.2-.8.2-.8 2-.8-2-.8-.2.8-.2.8-2Z"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
    </svg>
  );
};

const Home = () => {
  return (
    <SiteLayout>
      <section className="hero-section">
          <div className="hero-mosaic">
            <div aria-hidden="true" className="hero-mosaic__grid">
              {homeHeroMosaicVideos.map((video, index) => (
                <div
                  className={`hero-mosaic__tile hero-mosaic__tile--${video.layout}`}
                  key={video.src}
                >
                  <video
                    autoPlay
                    className="hero-mosaic__media"
                    disablePictureInPicture
                    loop
                    muted
                    playsInline
                    preload={index < 2 ? "auto" : "metadata"}
                  >
                    <source src={video.src} type="video/mp4" />
                  </video>
                </div>
              ))}
            </div>

            <div className="hero-mosaic__overlay" />

            <div className="hero-copy hero-copy--statement">
              <h1>Grey is a privilege, not an age</h1>
              <span className="hero-divider" />
              <p className="lead">
                Helping women embrace their natural grey and discover the colours
                that make them feel most like themselves.
              </p>
              <div className="hero-services">
                {homeHeroServiceLabels.map((item, index) => (
                  <React.Fragment key={item}>
                    {index > 0 && <span className="hero-services__separator">•</span>}
                    <span>{item}</span>
                  </React.Fragment>
                ))}
              </div>
              <ActionGroup actions={homeHeroActions} className="hero-actions" />
            </div>
          </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="section-heading">
            <p className="section-eyebrow">Introduction</p>
            <h2>A specialist studio for women embracing natural grey</h2>
            <p className="body-copy">
              Lizzie Fox Colour Studio helps women move toward grey blending,
              seasonal colour clarity, and low-maintenance luxury colour with a
              more personal, confidence-led approach. Every service is designed
              to feel elevated, authentic, and easy to live with.
            </p>
          </div>

          <div className="stats-grid">
            {homeIntroductionCards.map((card) => (
              <article className="intro-card" key={card.title}>
                <div className="intro-card__icon">
                  <IntroductionIcon icon={card.icon} />
                </div>
                <p className="intro-card__eyebrow">{card.title}</p>
                <p className="intro-card__body">{card.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container split-layout split-layout--visual">
          <div className="">
            <img alt="Lizzie with client in studio" src="/images/lizzie-about.webp" />
          </div>

          <div>
            <div className="section-heading">
              <p className="section-eyebrow">About Lizzie</p>
              <h2>Grey Transition Specialist. Colour Analyst. Over 22 years of experience.</h2>
            </div>

            <div className="story-copy">
              {lizzieStory.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section section--tinted">
        <div className="container">
          <div className="section-heading">
            <p className="section-eyebrow">Client Transformations</p>
            <h2>Grey transition journeys with polish and presence</h2>
          </div>

          <div className="home-transformations-grid">
            {homeTransformationGallery.map((result) => (
              <article className="home-transformation-card" key={result.image}>
                <img alt={result.alt} src={result.image} />
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="section-heading">
            <p className="section-eyebrow">Testimonials</p>
            <h2>Trusted by women who want colour confidence</h2>
          </div>

          <div className="testimonial-grid">
            {testimonials.map((testimonial) => (
              <TestimonialCard key={testimonial.author} testimonial={testimonial} />
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="section-heading">
            <p className="section-eyebrow">FAQs</p>
            <h2>Frequently asked questions</h2>
          </div>
          <FaqList items={homeFaqs} />
        </div>
      </section>
    </SiteLayout>
  );
};

export default Home;
